import React, { useState, useEffect, useCallback } from "react";
import { Input } from "antd";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  size?: "small" | "middle" | "large";
  className?: string;
  countryCode?: string;
  disabled?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = "",
  onChange,
  onBlur,
  placeholder = "Enter your phone number",
  size = "large",
  className = "",
  countryCode = "+1",
  disabled = false,
}) => {
  const [displayValue, setDisplayValue] = useState("");
  const [rawValue, setRawValue] = useState("");
  const phoneUtil = PhoneNumberUtil.getInstance();

  const getCountryIsoFromCode = useCallback((code: string): string => {
    const codeMap: { [key: string]: string } = {
      "+1": "US",
      "+7": "RU",
      "+20": "EG",
      "+27": "ZA",
      "+30": "GR",
      "+31": "NL",
      "+32": "BE",
      "+33": "FR",
      "+34": "ES",
      "+36": "HU",
      "+39": "IT",
      "+40": "RO",
      "+41": "CH",
      "+43": "AT",
      "+44": "GB",
      "+45": "DK",
      "+46": "SE",
      "+47": "NO",
      "+48": "PL",
      "+49": "DE",
      "+55": "BR",
      "+60": "MY",
      "+61": "AU",
      "+62": "ID",
      "+63": "PH",
      "+64": "NZ",
      "+65": "SG",
      "+66": "TH",
      "+81": "JP",
      "+82": "KR",
      "+84": "VN",
      "+86": "CN",
      "+90": "TR",
      "+91": "IN",
      "+92": "PK",
      "+93": "AF",
      "+94": "LK",
      "+95": "MM",
      "+98": "IR",
      "+212": "MA",
      "+213": "DZ",
      "+216": "TN",
      "+218": "LY",
      "+220": "GM",
      "+221": "SN",
      "+222": "MR",
      "+223": "ML",
      "+224": "GN",
      "+225": "CI",
      "+226": "BF",
      "+227": "NE",
      "+228": "TG",
      "+229": "BJ",
      "+230": "MU",
      "+231": "LR",
      "+232": "SL",
      "+233": "GH",
      "+234": "NG",
      "+235": "TD",
      "+236": "CF",
      "+237": "CM",
      "+238": "CV",
      "+239": "ST",
      "+240": "GQ",
      "+241": "GA",
      "+242": "CG",
      "+243": "CD",
      "+244": "AO",
      "+245": "GW",
      "+246": "IO",
      "+247": "AC",
      "+248": "SC",
      "+249": "SD",
      "+250": "RW",
      "+251": "ET",
      "+252": "SO",
      "+253": "DJ",
      "+254": "KE",
      "+255": "TZ",
      "+256": "UG",
      "+257": "BI",
      "+258": "MZ",
      "+260": "ZM",
      "+261": "MG",
      "+262": "RE",
      "+263": "ZW",
      "+264": "NA",
      "+265": "MW",
      "+266": "LS",
      "+267": "BW",
      "+268": "SZ",
      "+269": "KM",
      "+290": "SH",
      "+291": "ER",
      "+297": "AW",
      "+298": "FO",
      "+299": "GL",
      "+350": "GI",
      "+351": "PT",
      "+352": "LU",
      "+353": "IE",
      "+354": "IS",
      "+355": "AL",
      "+356": "MT",
      "+357": "CY",
      "+358": "FI",
      "+359": "BG",
      "+370": "LT",
      "+371": "LV",
      "+372": "EE",
      "+373": "MD",
      "+374": "AM",
      "+375": "BY",
      "+376": "AD",
      "+377": "MC",
      "+378": "SM",
      "+380": "UA",
      "+381": "RS",
      "+382": "ME",
      "+383": "XK",
      "+385": "HR",
      "+386": "SI",
      "+387": "BA",
      "+389": "MK",
      "+420": "CZ",
      "+421": "SK",
      "+423": "LI",
      "+500": "FK",
      "+501": "BZ",
      "+502": "GT",
      "+503": "SV",
      "+504": "HN",
      "+505": "NI",
      "+506": "CR",
      "+507": "PA",
      "+508": "PM",
      "+509": "HT",
      "+590": "GP",
      "+591": "BO",
      "+592": "GY",
      "+593": "EC",
      "+594": "GF",
      "+595": "PY",
      "+596": "MQ",
      "+597": "SR",
      "+598": "UY",
      "+599": "CW",
      "+852": "HK",
      "+853": "MO",
      "+855": "KH",
      "+856": "LA",
      "+880": "BD",
      "+886": "TW",
      "+961": "LB",
      "+962": "JO",
      "+963": "SY",
      "+964": "IQ",
      "+965": "KW",
      "+966": "SA",
      "+967": "YE",
      "+968": "OM",
      "+970": "PS",
      "+971": "AE",
      "+972": "IL",
      "+973": "BH",
      "+974": "QA",
      "+975": "BT",
      "+976": "MN",
      "+977": "NP",
      "+992": "TJ",
      "+993": "TM",
      "+994": "AZ",
      "+995": "GE",
      "+996": "KG",
      "+998": "UZ",
    };
    return codeMap[code] || "US";
  }, []);

  const stripNonNumeric = useCallback((input: string): string => {
    return input.replace(/\D/g, "");
  }, []);

  const formatPhoneNumber = useCallback(
    (phoneNumber: string, countryIso: string): string => {
      if (!phoneNumber) return "";

      try {
        const national = phoneNumber.replace(/^0+/, "");
        const fullNumber = `${countryCode}${national}`;
        const number = phoneUtil.parseAndKeepRawInput(fullNumber, countryIso);

        if (phoneUtil.isValidNumber(number)) {
          const formatted = phoneUtil.format(
            number,
            PhoneNumberFormat.NATIONAL
          );
          return formatted.replace(/^0\s?/, "");
        }

        return formatFallback(national, countryIso);
      } catch (error) {
        return formatFallback(phoneNumber, countryIso);
      }
    },
    [countryCode, phoneUtil]
  );

  const formatFallback = useCallback(
    (phoneNumber: string, countryIso: string): string => {
      if (!phoneNumber) return "";

      const patterns: { [key: string]: (num: string) => string } = {
        US: (num) => {
          if (num.length <= 3) return num;
          if (num.length <= 6) return `(${num.slice(0, 3)}) ${num.slice(3)}`;
          return `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6, 10)}`;
        },
        IN: (num) => {
          if (num.length <= 5) return num;
          if (num.length <= 8) return `${num.slice(0, 5)} ${num.slice(5)}`;
          return `${num.slice(0, 5)} ${num.slice(5, 8)} ${num.slice(8)}`;
        },
        GB: (num) => {
          if (num.length <= 4) return num;
          if (num.length <= 7) return `${num.slice(0, 4)} ${num.slice(4)}`;
          return `${num.slice(0, 4)} ${num.slice(4, 7)} ${num.slice(7)}`;
        },
      };

      const formatter = patterns[countryIso];
      return formatter ? formatter(phoneNumber) : phoneNumber;
    },
    []
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const stripped = stripNonNumeric(inputValue);
      const countryIso = getCountryIsoFromCode(countryCode);
      try {
        const phoneNumberWithCode = `${countryCode}${stripped}`;
        const parsedNumber = phoneUtil.parseAndKeepRawInput(
          phoneNumberWithCode,
          countryIso
        );
        const nationalNumber = parsedNumber.getNationalNumber().toString();

        const exampleNumber = phoneUtil.getExampleNumber(countryIso);
        const maxLength = exampleNumber
          ? exampleNumber.getNationalNumber().toString().length
          : 10;
        const truncatedNumber = nationalNumber.slice(0, maxLength);
        setRawValue(truncatedNumber);
        const formatted = formatPhoneNumber(truncatedNumber, countryIso);
        setDisplayValue(formatted);
        if (onChange) {
          onChange(truncatedNumber);
        }
      } catch {
        setRawValue(stripped);
        const formatted = formatPhoneNumber(stripped, countryIso);
        setDisplayValue(formatted);
        if (onChange) {
          onChange(stripped);
        }
      }
    },
    [
      countryCode,
      stripNonNumeric,
      formatPhoneNumber,
      getCountryIsoFromCode,
      onChange,
      phoneUtil,
    ]
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  useEffect(() => {
    if (value !== rawValue) {
      const stripped = stripNonNumeric(value);
      const countryIso = getCountryIsoFromCode(countryCode);
      setRawValue(stripped);

      if (stripped) {
        const formatted = formatPhoneNumber(stripped, countryIso);
        setDisplayValue(formatted);
      } else {
        setDisplayValue("");
      }
    }
  }, [
    value,
    countryCode,
    rawValue,
    stripNonNumeric,
    formatPhoneNumber,
    getCountryIsoFromCode,
  ]);

  useEffect(() => {
    if (rawValue) {
      const countryIso = getCountryIsoFromCode(countryCode);
      const formatted = formatPhoneNumber(rawValue, countryIso);
      setDisplayValue(formatted);
    }
  }, [countryCode, rawValue, formatPhoneNumber, getCountryIsoFromCode]);

  return (
    <Input
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      size={size}
      className={className}
      disabled={disabled}
    />
  );
};

export default PhoneInput;
